import { Fragment } from "react";
import { LinkItem } from "../link-item";
import * as Styled from "./style";
import { isVideoLegacySeason } from "~/components/blocks/product/type-guard";
import { useRouterForVodList } from "~/hooks/use-router-for-vod-list";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { isEmptyObject } from "~/util/is-empty-object";

type NotCastProps = {
  /** videoクエリを叩いて取得したデータ */
  videoData: G_GetVideoQuery["video"];
  /** 出演者の情報が存在するかどうか */
  hasCastData: boolean;
  /** グラビアかどうか(デフォルトは `false`) */
  isGravure?: boolean;
};

type Staff = { id: string; staffName: string };
type NormalizedStaff = { roleName: string; staffs: Staff[] };

/** 作品情報ブロック | 出演者（Cast）以外の情報を描画するコンポーネント */
export const NotCast = (props: NotCastProps) => {
  const { videoData, hasCastData, isGravure = false } = props;

  const { buildVodListHref } = useRouterForVodList();

  const { staffs, genres, copyright, rating, categories } = videoData;

  const groupByStaff = staffs.reduce((prevStaffs, { roleName, staffName, person }) => {
    const targetIndex = prevStaffs.findIndex((prev) => prev.roleName === roleName);
    if (targetIndex !== -1) {
      prevStaffs[targetIndex].staffs.push({ id: person.id, staffName: staffName });
    } else {
      prevStaffs.push({ roleName, staffs: [{ id: person.id, staffName: staffName }] });
    }
    return prevStaffs;
  }, [] as NormalizedStaff[]);

  const generateVodListStaffsPageHref = (id: string) => {
    const isRatingProduct = rating.category === "R15" || rating.category === "R18";
    const query = isRatingProduct
      ? { staffs: `${id}`, categories: categories.map(({ id }) => id).join(",") }
      : { staffs: `${id}` };

    return buildVodListHref({ query, isGravure });
  };

  const generateSeriesPageHref = (id: string) => buildVodListHref({ query: { series: id }, isGravure });

  const generateVodListGenrePageHref = (id: string) => buildVodListHref({ query: { genres: id }, isGravure });

  return (
    <Styled.DivNotCastWrapper hasCastData={hasCastData}>
      {0 < groupByStaff.length && (
        <Styled.DivWrapper>
          {groupByStaff.map(({ roleName, staffs }, idx) => (
            <Fragment key={`${roleName}-${idx}`}>
              <Styled.Heading>{roleName}</Styled.Heading>
              {staffs.map(({ id, staffName }) => {
                const href = generateVodListStaffsPageHref(id);
                return (
                  <Styled.DivLinkWrapper key={id}>
                    <LinkItem href={href}>
                      <Styled.DivItemText>{staffName}</Styled.DivItemText>
                    </LinkItem>
                  </Styled.DivLinkWrapper>
                );
              })}
            </Fragment>
          ))}
        </Styled.DivWrapper>
      )}

      {isVideoLegacySeason(videoData) && videoData.series && !isEmptyObject(videoData.series ?? {}) && (
        <Styled.DivWrapper>
          <Styled.Heading>シリーズ</Styled.Heading>
          <Styled.DivLinkWrapper>
            <LinkItem href={generateSeriesPageHref(videoData.series.id)}>
              <Styled.DivItemText>{videoData.series.name}</Styled.DivItemText>
            </LinkItem>
          </Styled.DivLinkWrapper>
        </Styled.DivWrapper>
      )}

      {0 < genres.length && (
        <Styled.DivWrapper>
          <Styled.Heading>ジャンル</Styled.Heading>
          {genres.map(({ id, name }) => {
            const href = generateVodListGenrePageHref(id);
            return (
              <Styled.DivLinkWrapper key={id}>
                <LinkItem href={href}>
                  <Styled.DivItemText>{name}</Styled.DivItemText>
                </LinkItem>
              </Styled.DivLinkWrapper>
            );
          })}
        </Styled.DivWrapper>
      )}

      {copyright !== "" && <Styled.ParCopyRight>{copyright}</Styled.ParCopyRight>}
    </Styled.DivNotCastWrapper>
  );
};
